import { Box, Center, Flex, HStack, Spinner, Tooltip } from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import { useIsRevelioAdmin } from '@revelio/auth';
import { FeatureFlag } from '@revelio/core';
import { Button, DetailsWithDescription, Option, Text } from '@revelio/ui';

import {
  CircleCheckEmptyIcon,
  CircleCheckedIcon,
  DeliverableIcons,
  DeliverablesIconLookup,
} from '../../../shared/ui/icons';
import { useUnleashFlag } from '../../../shared/unleash-flags/use-unleash-flag';
import { useClientDataBuilderConfig } from '../api/useClientDataBuilderConfig';
import { DataBuilderFooter } from '../components/DataBuilderFooter';
import { DataBuilderStepLayout } from '../components/ui/DataBuilderStepLayout';
import {
  type DeliveryFormat,
  type DeliveryMethod,
  selectDeliveryConfigured,
  selectDeliveryFormat,
  selectDeliveryMethod,
  selectS3Config,
  selectSftpConfig,
  selectSnowflakeConfig,
  useDataBuilderActions,
  useDataBuilderSelector,
} from '../data-builder.store';
import { useDataBuilderNavigation } from '../useDataBuilderNavigation';
import { DownloadConfigModal } from './step3-delivery/DownloadConfigModal';
import { S3ConfigModal } from './step3-delivery/S3ConfigModal';
import { SftpConfigModal } from './step3-delivery/SftpConfigModal';
import { SnowflakeConfigModal } from './step3-delivery/SnowflakeConfigModal';
import { useVerifyDataBuilder } from './step3-delivery/useVerifyDataBuilder';

const DELIVERY_METHOD_OPTIONS: {
  value: DeliveryMethod;
  label: string;
  detail: string;
  icon: DeliverableIcons;
}[] = [
  {
    value: 'download',
    label: 'Download',
    detail:
      "Receive your data as a zipped package that contains all the files you've selected",
    icon: DeliverableIcons.DOWNLOAD,
  },
  {
    value: 'snowflake',
    label: 'Snowflake Database',
    detail: 'Receive your data in Snowflake.',
    icon: DeliverableIcons.SNOWFLAKE,
  },
  {
    value: 's3',
    label: 'AWS S3 Bucket',
    detail: 'Receive your data in a private S3 Bucket.',
    icon: DeliverableIcons.AWS_S3_BUCKET,
  },
  {
    value: 'sftp',
    label: 'SFTP',
    detail: 'Receive your data via SFTP.',
    icon: DeliverableIcons.API,
  },
];

type DeliveryMethodOptionProps = {
  value: DeliveryMethod;
  label: string;
  detail: string;
  icon: DeliverableIcons;
  isSelected: boolean;
  disabled?: boolean;
  onClick: () => void;
  onEdit?: () => void;
  showEditButton?: boolean;
};

const DeliveryMethodOption = ({
  value,
  label,
  detail,
  icon,
  isSelected,
  disabled,
  onClick,
  onEdit,
  showEditButton,
}: DeliveryMethodOptionProps) => {
  const Icon = isSelected ? CircleCheckedIcon : CircleCheckEmptyIcon;
  const DeliveryIcon = DeliverablesIconLookup[icon];

  return (
    <Option
      data-testid={`data-builder-delivery-method-${value}`}
      style={{ minHeight: 0, alignItems: 'flex-start' }}
      isSelected={isSelected}
      disabled={disabled}
      onClick={onClick}
      startSlot={
        <Flex align="center" gap="3">
          <Icon size={20} />
          {DeliveryIcon !== undefined && (
            <Box bg="#E5EBF1" p="12px" borderRadius="50%">
              <Center>
                <DeliveryIcon boxSize={6} />
              </Center>
            </Box>
          )}
        </Flex>
      }
      endSlot={
        showEditButton ? (
          <Button
            size="sm"
            variant="outlined"
            onClick={(event) => {
              event.stopPropagation();
              if (onEdit !== undefined) {
                onEdit();
              }
            }}
          >
            Edit
          </Button>
        ) : undefined
      }
    >
      <DetailsWithDescription title={label} detail={detail} />
    </Option>
  );
};

export const DataBuilderDeliveryStep = () => {
  const { isRevelioAdmin } = useIsRevelioAdmin();
  const isDataBuilderSftpEnabled = useUnleashFlag(FeatureFlag.DataBuilderSftp);
  const clientDataBuilderConfig = useClientDataBuilderConfig();
  const deliveryMethod = useDataBuilderSelector(selectDeliveryMethod);
  const deliveryFormat = useDataBuilderSelector(selectDeliveryFormat);
  const deliveryConfigured = useDataBuilderSelector(selectDeliveryConfigured);
  const snowflakeConfig = useDataBuilderSelector(selectSnowflakeConfig);
  const s3Config = useDataBuilderSelector(selectS3Config);
  const sftpConfig = useDataBuilderSelector(selectSftpConfig);
  const { goToNextStep } = useDataBuilderNavigation();
  const {
    setDeliveryMethod,
    setDeliveryFormat,
    setSnowflakeConfig,
    setS3Config,
    setSftpConfig,
  } = useDataBuilderActions();
  const [activeConfigModal, setActiveConfigModal] =
    useState<DeliveryMethod | null>(null);
  const {
    isPending: isVerifying,
    isError: isVerifyError,
    errorMessage: verifyErrorMessage,
    isReady: isVerifyReady,
    isWaitingForCompanies,
  } = useVerifyDataBuilder();

  const continueDisabledReason = (() => {
    if (!deliveryConfigured) {
      return 'Configure your delivery method to continue.';
    }
    if (isWaitingForCompanies) {
      return 'Loading companies...';
    }
    if (isVerifying) {
      return 'Verifying configuration...';
    }
    if (isVerifyError) {
      return 'Resolve the configuration issue to continue.';
    }
    if (!isVerifyReady) {
      return 'Verifying configuration...';
    }
    return '';
  })();
  const isContinueDisabled = continueDisabledReason !== '';

  useEffect(() => {
    if (isDataBuilderSftpEnabled || deliveryMethod !== 'sftp') {
      return;
    }

    setDeliveryMethod('download');
    setSftpConfig(null);
    setActiveConfigModal(null);
  }, [
    deliveryMethod,
    isDataBuilderSftpEnabled,
    setDeliveryMethod,
    setSftpConfig,
  ]);

  const hasS3DefaultLocation =
    clientDataBuilderConfig?.s3_location !== undefined &&
    clientDataBuilderConfig.s3_location !== null;
  const hasSnowflakeDefaultLocation =
    clientDataBuilderConfig?.snowflake_location !== undefined &&
    clientDataBuilderConfig.snowflake_location !== null;

  const deliveryOptions = DELIVERY_METHOD_OPTIONS.filter((option) => {
    return isDataBuilderSftpEnabled || option.value !== 'sftp';
  }).map((option) => {
    if (isRevelioAdmin) {
      return { ...option, disabled: false };
    }
    if (option.value === 'snowflake') {
      return { ...option, disabled: !hasSnowflakeDefaultLocation };
    }
    if (option.value === 's3') {
      return { ...option, disabled: !hasS3DefaultLocation };
    }
    if (option.value === 'sftp') {
      return { ...option, disabled: true };
    }
    return { ...option, disabled: false };
  });

  const selectedMethodIsConfigured = (() => {
    if (deliveryMethod === 'snowflake') {
      return snowflakeConfig !== null;
    }
    if (deliveryMethod === 's3') {
      return s3Config !== null;
    }
    if (deliveryMethod === 'sftp') {
      return sftpConfig !== null;
    }
    return true;
  })();

  const isMethodConfigured = (method: DeliveryMethod) => {
    if (method === 'snowflake') {
      return snowflakeConfig !== null;
    }
    if (method === 's3') {
      return s3Config !== null;
    }
    if (method === 'sftp') {
      return sftpConfig !== null;
    }
    return true;
  };

  const closeActiveModal = () => {
    setActiveConfigModal(null);
  };

  const saveDownloadConfig = (format: DeliveryFormat): void => {
    setDeliveryMethod('download');
    setDeliveryFormat(format);
    setActiveConfigModal(null);
  };

  const saveSnowflakeConfig = (
    value: NonNullable<typeof snowflakeConfig>
  ): void => {
    setDeliveryMethod('snowflake');
    setSnowflakeConfig(value);
    setActiveConfigModal(null);
  };

  const saveS3Config = (value: NonNullable<typeof s3Config>): void => {
    setDeliveryMethod('s3');
    setS3Config(value);
    setActiveConfigModal(null);
  };

  const saveSftpConfig = (value: NonNullable<typeof sftpConfig>): void => {
    setDeliveryMethod('sftp');
    setSftpConfig(value);
    setActiveConfigModal(null);
  };

  const selectMethod = (method: DeliveryMethod) => {
    if (method === deliveryMethod) {
      return;
    }
    if (method === 'download') {
      setActiveConfigModal('download');
      return;
    }
    if (!isMethodConfigured(method)) {
      setActiveConfigModal(method);
      return;
    }
    setDeliveryMethod(method);
  };

  return (
    <DataBuilderStepLayout
      title="Delivery"
      footer={
        <DataBuilderFooter
          actions={
            <Tooltip
              label={continueDisabledReason}
              isDisabled={!isContinueDisabled}
              hasArrow
              placement="top"
              shouldWrapChildren
            >
              <Button
                onClick={goToNextStep}
                disabled={isContinueDisabled}
                color="primary"
                size="sm"
              >
                <HStack gap={2}>
                  {(isVerifying || isWaitingForCompanies) && (
                    <Spinner size="xs" label="" />
                  )}
                  <span>Continue</span>
                </HStack>
              </Button>
            </Tooltip>
          }
        />
      }
    >
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
        data-testid="data-builder-delivery-step"
      >
        <section
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <Text as="h3" fontSize="sm" weight="semibold">
            Delivery Method
          </Text>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr)',
              gap: '12px',
            }}
          >
            {deliveryOptions.map((option) => (
              <DeliveryMethodOption
                key={option.value}
                value={option.value}
                label={option.label}
                detail={option.detail}
                icon={option.icon}
                isSelected={deliveryMethod === option.value}
                disabled={option.disabled}
                showEditButton={
                  deliveryMethod === option.value && selectedMethodIsConfigured
                }
                onEdit={() => {
                  setActiveConfigModal(option.value);
                }}
                onClick={() => selectMethod(option.value)}
              />
            ))}
          </div>
          {isVerifyError && (
            <div style={{ paddingTop: '0.5rem' }}>
              <Text
                as="h3"
                fontSize="bg"
                weight="semibold"
                includePadding
                style={{ color: 'var(--ui-text-error)' }}
              >
                Submission Prevented
              </Text>
              <Text
                data-testid="data-builder-verify-error"
                style={{ color: 'var(--ui-text-error)' }}
              >
                {verifyErrorMessage}
              </Text>
            </div>
          )}
        </section>
      </div>

      <DownloadConfigModal
        isOpen={activeConfigModal === 'download'}
        onClose={closeActiveModal}
        initialValue={deliveryFormat}
        onSave={saveDownloadConfig}
      />
      <SnowflakeConfigModal
        isOpen={activeConfigModal === 'snowflake'}
        onClose={closeActiveModal}
        initialValue={snowflakeConfig}
        defaultDatabase={
          clientDataBuilderConfig?.snowflake_location?.snowflake_db ?? null
        }
        defaultSchema={
          clientDataBuilderConfig?.snowflake_location?.snowflake_schema ?? null
        }
        onSave={saveSnowflakeConfig}
      />
      <S3ConfigModal
        isOpen={activeConfigModal === 's3'}
        onClose={closeActiveModal}
        initialValue={s3Config}
        defaultBucket={clientDataBuilderConfig?.s3_location?.s3_bucket ?? null}
        onSave={saveS3Config}
      />
      {isDataBuilderSftpEnabled && (
        <SftpConfigModal
          isOpen={activeConfigModal === 'sftp'}
          onClose={closeActiveModal}
          initialValue={sftpConfig}
          onSave={saveSftpConfig}
        />
      )}
    </DataBuilderStepLayout>
  );
};
